/*
 * Phone verification is restricted to the US and Canada. SMS codes are only
 * sent to these regions — international routes were abused for SMS pumping
 * (Aug 2026: bot accounts registered numbers on high-payout termination
 * routes and drained the SMS budget). This mirrors the backend
 * `accounts.services.phone.check_phone_country` guard so the UI rejects the
 * same numbers before a code is ever requested.
 *
 * Both countries share the +1 country code (NANP), so a plain "+1" prefix is
 * not enough — many premium-rate Caribbean routes are +1 too. We accept +1
 * and reject the non-US/CA NANP area codes explicitly.
 */

// Blocked NANP area codes (Caribbean + Pacific territories). Kept in sync
// with the `NANP` map in system/core/utils/phone.py, minus the CA block and
// minus Puerto Rico (787/939) — allowed as US-market members.
const NON_US_CA_NANP_AREA_CODES = new Set([
  '684', '264', '268', '242', '246', '441', '284', '340', '345', '767',
  '473', '671', '664', '670', '869', '758', '784', '721', '868', '649',
  '876', '658', '809', '829', '849',
]);

/*
 * Return true if `raw` is a valid-looking US/Canadian mobile number.
 * Accepts values with or without a leading "+", and with formatting
 * separators (react-phone-input-2 emits "+1 (202) 555-0123" style text).
 */
export const isUsCaPhone = (raw) => {
  const digits = (raw || '').replace(/\D/g, '');

  // NANP: country code 1 + 10 national digits.
  if (digits.length !== 11 || digits[0] !== '1') {
    return false;
  }

  const areaCode = digits.slice(1, 4);
  return !NON_US_CA_NANP_AREA_CODES.has(areaCode);
};

export const PHONE_COUNTRY_ERROR = 'Only US and Canada phone numbers are supported.';

const phone = {
  isUsCaPhone,
  PHONE_COUNTRY_ERROR,
};

export default phone;
